/* eslint-disable react-hooks/exhaustive-deps */

import { PaginationDots } from "@/components/common";
import { Card, CardContent } from "@/components/ui/card";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { StarIcon } from "lucide-react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import React, { useEffect, useState } from "react";

interface MobileCarouselProps {
  testimonials: {
    id: number;
    quote: string;
    description: string;
    author: string;
    rating: number;
    authorStoreName?: string;
    authorStoreType?: string;
    authorAvatar: string;
  }[];
}

export default function MobileCarousel({
  testimonials,
}: MobileCarouselProps): React.ReactNode {
  const pathname = usePathname(); // Get current URL path

  const [mobileApi, setMobileApi] = useState<CarouselApi | null>(null);
  const [mobileCurrent, setMobileCurrent] = useState(0);

  useEffect(() => {
    if (testimonials.length <= 1) return undefined;
    const interval = setInterval(() => {
      mobileApi?.scrollTo(mobileCurrent + 1);
    }, 3000); // 5000ms = 5 seconds

    return () => clearInterval(interval); // Cleanup on unmount
  }, [mobileCurrent, testimonials.length]);

  useEffect(() => {
    if (!mobileApi) return undefined;
    const onSelect = () => setMobileCurrent(mobileApi.selectedScrollSnap());
    onSelect();
    mobileApi.on("select", onSelect);
    return () => {
      mobileApi.off("select", onSelect);
    };
  }, [mobileApi]);

  return (
    <>
      <Carousel
        opts={{ align: "center", loop: testimonials.length > 1 }}
        className="w-full lg:hidden "
        setApi={setMobileApi}
        aria-label="Customer testimonials"
      >
        <CarouselContent className="px-0">
          {testimonials.map((testimonial) => (
            <CarouselItem
              key={testimonial.id}
              className={
                testimonials.length > 1
                  ? "basis-auto p-0  pr-8 px-6 xs:px-0 xs:pr-3"
                  : "basis-auto p-0 px-6 flex justify-center"
              }
            >
              <Card className="bg-white rounded-lg border h-full border-solid border-[#fdd9d2] w-[87vw] xs:w-[20rem] ">
                <CardContent className="p-4 flex flex-col justify-between h-full gap-4 pb-0">
                  <div className="flex flex-col gap-4">
                    <Image
                      width={60}
                      height={10}
                      className="w-[59.47px] h-10"
                      alt="Quote"
                      src="/images/quote.png"
                    />

                    <div className="flex flex-col gap-2">
                      <h3 className="font-medium text-secondary-6 ">
                        {testimonial.quote}
                      </h3>

                      <p className="font-normal text-muted-2-foreground text-sm tracking-[0] leading-5 line-clamp-5">
                        {testimonial.description}
                      </p>
                    </div>
                  </div>

                  {testimonial.author && (
                  <div className="flex items-start gap-3 border-t pt-4 border-[#fdd9d2]">
                    <div className="flex flex-col w-full">
                      <span className="font-normal text-black text-base tracking-[-0.16px] leading-6">
                        {testimonial.author}
                      </span>

                      {(testimonial.authorStoreName || testimonial.authorStoreType) && (
                        <div className="flex justify-between items-center w-full text-muted-2-foreground">
                          <p>{testimonial.authorStoreName}</p>
                          <p className="sm:block hidden">
                            {testimonial.authorStoreType}
                          </p>
                        </div>
                      )}
                      {pathname === "/customers" && (
                        <div className="flex items-center gap-1.5 py-2">
                          <div className="flex">
                            {[...Array(5)].map((_, i) => (
                              <StarIcon
                                key={i}
                                className="w-4 h-4 fill-amber-500 text-transparent"
                              />
                            ))}
                          </div>
                          <span className="text-warning-1 text-xs font-semibold">
                            {testimonial.rating}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  )}
                </CardContent>
              </Card>
            </CarouselItem>
          ))}
        </CarouselContent>
      {testimonials.length > 1 && (
        <PaginationDots
          total={testimonials.length}
          current={mobileCurrent}
          onDotClick={(index) => mobileApi?.scrollTo(index)}
          variant="dark"
        />
      )}
      </Carousel>

    </>
  );
}
